import React, { useEffect, useState } from 'react'
import { FlatList, Text, View } from 'react-native'
import { TemplatePage } from '../../../components/TemplatePage'
import { globalStyle } from '../../../theme/global'
import { HeaderList } from '../components/HeaderList'
import { Separator } from '../../../components/Separator'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { usePost } from '../../../hooks/usePost'
import { paths } from '../../../api/pathsApi'
import { ControlServiceRecord, Item } from '../../../context/Alerts/types'
import { ReqControlService, ResControlService } from '../../../types'
import { DescriptionControlService } from '../components/DescriptionControlService'
import { ItemControlService } from '../components/ItemControlService'
import { useTranslation } from 'react-i18next'
import { AlertMessage } from '../../../components/AlertMessage'
import { Alert401 } from '../../../components/AlertMessage/Alert401'
import { useContextApp } from '../../../hooks/useContextApp'
import { RootParams } from '../../../navigator/types'
import { textColor } from '../../../theme/colors'

interface Navigate extends StackNavigationProp<RootParams, 'ControlServicesStack'> {}

const FIRST_ELEMENT = 0

export const DetailControlServices = () => {
  const {
    handleLogOut,
    getRecordsByArea,
    deleteControlSevice,
    seaWaterContext,
    controlServicesContext,
  } = useContextApp()

  const { navigate } = useNavigation<Navigate>()

  const { t } = useTranslation()

  const [showAlertDelete, setShowAlertDelete] = useState(false)
  const [showAlertSend, setShowAlertSend] = useState(false)
  const [showAlertResponse, setShowAlertResponse] = useState(false)
  const [error401, setError401] = useState<{ code: number | null; msg: string }>({
    code: null,
    msg: '',
  })
  const handleConfirmDelete = () => setShowAlertDelete(true)
  const handleConfirmSend = () => setShowAlertSend(true)
  const handleShowAlertResponse = () => setShowAlertResponse(true)

  const [showErrorAlert, setShowErrorAlert] = useState({
    show: false,
    message: '',
  })
  const [showDuplicatedAlert, setShowDuplicatedAlert] = useState({
    show: false,
    message: '',
  })

  const controlServiceRecords = getRecordsByArea({
    alertType: 'controlServiceRecords',
    areaSelected: 'seaWater',
    getArea: (item: Item<ControlServiceRecord>) => item.area,
    getRecord: (item: Item<ControlServiceRecord>) => item.record,
  })
  const [selected, setSelected] = useState<{
    [key: string]: ControlServiceRecord
  }>({})

  const { handleSendData, isLoading } = usePost<ReqControlService[], ResControlService>({
    url: paths().CONTROL_SERVICES.SAVE_DATA,
    onSuccess(data, body) {
      if ('success' in data && data.success && body[0] && body[0].confirm) {
        handleDelete()
        handleShowAlertResponse()
        setSelected({})
        return
      }

      if ('success' in data && data.success && body[0] && !body[0].confirm) {
        handleSend({ confirm: true })
        return
      }

      if ('warning' in data && body[0] && !body[0].confirm) {
        const { warning } = data
        setShowDuplicatedAlert({
          show: true,
          message: `${t(warning[0]!.type)}. ${t('Embarcación')}: ${warning[0]!.boarding}. ${t(
            'Centro',
          )}: ${warning[0]!.center}. ${t('Fecha')}: ${warning[0]!.date}`,
        })
        return
      }

      if ('warning' in data && body[0] && body[0].confirm) {
        handleDelete()
        handleShowAlertResponse()
        setSelected({})
        return
      }
    },

    onError(error) {
      if (error.code === 401) {
        seaWaterContext.handleResetData()
        controlServicesContext.handleResetData()
        setError401(error)
        return
      }
      setShowErrorAlert({ show: true, message: error.msg })
    },
  })

  const handleItemSelected = (record: ControlServiceRecord) => {
    setSelected((oldSelected) => {
      const { [record.formKey]: deleteRecord, ...rest } = oldSelected
      if (deleteRecord) {
        return {
          ...rest,
        }
      }
      return {
        ...oldSelected,
        [record.formKey]: record,
      }
    })
  }

  const handleDelete = () => {
    const records = Object.values(selected).map((value) => value.formKey)
    deleteControlSevice(records)
    setSelected({})
  }

  const handleSend = ({ confirm }: { confirm: boolean }) => {
    let itemsToSend: ReqControlService[] = []
    Object.values(selected).forEach((item, index) => {
      if (item.categoryId === 1) {
        const category1ItemsProcessed = Object.keys(item.centersCategoryOne).map((centerIdItem) => {
          const paramInfo = item.centersCategoryOne[Number(centerIdItem)]
          return {
            ...item,
            centerId: Number(centerIdItem),
            ...paramInfo,
            confirm,
            valid: false,
            choreId: paramInfo ? paramInfo.choreId : 0,
            observation: paramInfo ? paramInfo.observation : '',
            ceco: paramInfo ? paramInfo.ceco === 'CECO' : false,
            percentage: paramInfo ? paramInfo.percentage : 0,
          }
        }) as ControlServiceRecord[]
        itemsToSend = itemsToSend.concat(category1ItemsProcessed)
      } else {
        itemsToSend.push({
          ...item,
          confirm,
        })
      }
      if (index === Object.values(selected).length - 1) {
        handleSendData(itemsToSend)
      }
    })
  }

  const handleShow = () => {
    const record = Object.values(selected)[FIRST_ELEMENT]

    if (record) {
      navigate('ControlServicesStack', {
        screen: 'ControlServices',
        params: {
          ...record,
          centersPercentageTotal: 0,
          boardingName: record.serviceName,
          currentCenter: 0,
          observation: record.observation ?? '',
          ceco: String(record.ceco),
        },
      })
    }
  }

  useEffect(() => {
    if (controlServiceRecords.length === 0) navigate('Alerts')
  }, [controlServiceRecords])

  return (
    <TemplatePage styleContent={{ padding: 0, marginBottom: 0 }} header title={'ALERTAS'}>
      <View style={globalStyle.viewContainer}>
        <FlatList
          data={controlServiceRecords}
          renderItem={({ item, index }) => (
            <ItemControlService
              key={index}
              registerKey={item.formKey}
              date={item.dateTime}
              name={item.serviceName}
              item={item}
              isCheck={!!selected[item.formKey]}
              getItemValue={(value) => handleItemSelected(value)}
            >
              <DescriptionControlService date={item.date} />
            </ItemControlService>
          )}
          ListHeaderComponent={() => (
            <HeaderList
              onPressDelete={handleConfirmDelete}
              onPressSend={handleConfirmSend}
              onPressShow={handleShow}
              sendingLoader={isLoading}
              numberOfSelectedRecords={Object.values(selected).length}
            />
          )}
          ItemSeparatorComponent={() => <Separator />}
        />
      </View>
      <Alert401 isRender={error401.code === 401} action={handleLogOut} />

      {showAlertDelete && (
        <AlertMessage
          title={t('Eliminar registros')}
          action={() => {
            handleDelete()
            setShowAlertDelete(false)
          }}
          cancelButton
          actionCancelButton={() => setShowAlertDelete(false)}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('¿Estás seguro de eliminar los registros seleccionados?')}
          </Text>
        </AlertMessage>
      )}

      {showAlertSend && (
        <AlertMessage
          title={t('Enviar')}
          action={() => {
            handleSend({ confirm: false })
            setShowAlertSend(false)
          }}
          cancelButton
          actionCancelButton={() => setShowAlertSend(false)}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('¿Estás seguro de enviar los registros seleccionados?')}
          </Text>
        </AlertMessage>
      )}

      {showAlertResponse && (
        <AlertMessage
          title={t('Solicitud Exitosa')}
          action={() => {
            setShowAlertSend(false)
          }}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('Formulario enviado correctamente')}
          </Text>
        </AlertMessage>
      )}
      {showDuplicatedAlert.show && (
        <AlertMessage
          title={t('Duplicado')}
          action={() => {
            handleSend({ confirm: true })
            setShowDuplicatedAlert({ show: false, message: '' })
          }}
          successLabel='Enviar'
          cancelButton
          actionCancelButton={() => setShowDuplicatedAlert({ show: false, message: '' })}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {showDuplicatedAlert.message}
          </Text>
        </AlertMessage>
      )}
      {showErrorAlert.show && (
        <AlertMessage
          title={t('Error')}
          action={() => setShowErrorAlert({ show: false, message: '' })}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>{showErrorAlert.message}</Text>
        </AlertMessage>
      )}
    </TemplatePage>
  )
}
